import locationApi from '@api/locationApi';
import IconLocation from '@assets/icons/location.svg?react';
import { useTranslator } from '@i18n';
import { rem } from '@mantine/core';
import { Spotlight, type SpotlightActionData } from '@mantine/spotlight';
import { locationSpotlightStore } from '@state/locationSpotlightStore';
import { IconSearch, IconWorld } from '@tabler/icons-react';
import { useRouterState } from '@tanstack/react-router';
import { useNavigate, useSearch } from '@tanstack/react-router';
import chain from '@util/chain';
import { themeColor } from '@util/color';
import orderBy from '@util/orderBy';

export default function LocationSpotlight() {
  const t = useTranslator();
  const routerState = useRouterState();
  const navigate = useNavigate();
  const [locations] = locationApi.all.useCache();
  const { locationId } = useSearch({ strict: false });

  const actions = chain(Object.values(locations ?? {}))
    .chain((_) => orderBy(_, [(x) => x.id !== locationId, 'name']))
    .chain((_) =>
      _.map<SpotlightActionData>(({ id, name, address }) => ({
        id,
        leftSection: <IconLocation />,
        label: name,
        description: [address?.street, [address?.zip, address?.city].filter(Boolean).join(' ')]
          .filter(Boolean)
          .join(', '),
        onClick: () => click(id),
        style:
          id === locationId
            ? {
                borderLeft: `4px solid ${themeColor('primary')}`,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
              }
            : undefined,
      })),
    )
    .value();

  function click(locationId: string) {
    const routePrefix = routerState.location.pathname.split('/')[1];
    let to;
    switch (routePrefix) {
      case '':
      case 'map':
      case 'inventory':
      case 'log':
        to = `/${routePrefix}`;
        break;

      default:
        to = '/';
    }

    navigate({ to, search: { locationId }, params: {} });

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  return (
    <Spotlight
      data-testid="location-select-popup"
      store={locationSpotlightStore}
      highlightQuery
      actions={[
        {
          id: '',
          leftSection: <IconWorld />,
          label: t('header.global'),
          onClick: () => click(''),
        },
        {
          group: t('locations.locations'),
          actions,
        },
      ]}
      searchProps={{
        leftSection: <IconSearch style={{ width: rem(20), height: rem(20) }} stroke={1.5} />,
        placeholder: t('locations.search'),
      }}
      nothingFound={t('locations.noResults')}
      scrollable
    />
  );
}
